import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  GET_CHAT_CAPABILITIES,
  resolveEvidenceCapabilities,
  type EvidenceCapabilities,
} from '../../contracts';
import { getChatCapabilities } from '../api/capabilities';
import { areCapabilitiesReportedByBackend, useIsAuthenticated } from '../injected';

/**
 * What Evidence chat can do in this deployment (MOD-1726, guardrail F9).
 *
 * The app decides at build time whether to ask (`capabilitiesReportedByBackend`
 * on `configureEvidenceData`); when it does not, no request is made and the
 * answer is unrestricted. When it does, the backend's answer is read once per
 * session, and every state short of a successful, well-formed body resolves
 * to the most restrictive value. Returns a resolved value, not the query.
 */
export function useQueryEvidenceCapabilities(): EvidenceCapabilities {
  const reportedByBackend = areCapabilitiesReportedByBackend();
  const isAuthenticated = useIsAuthenticated();
  const { data, isSuccess } = useQuery<unknown>({
    queryKey: [GET_CHAT_CAPABILITIES],
    queryFn: getChatCapabilities,
    enabled: reportedByBackend && isAuthenticated,
    // A posture changes when an appliance restarts, not mid-session.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const response = isSuccess ? data : undefined;

  return useMemo(
    () => resolveEvidenceCapabilities({ reportedByBackend, response }),
    [reportedByBackend, response]
  );
}
